import { FC, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import { defaultDuration } from 'src/components/DurationSelect/DurationSelect';
import { useIdleTimer } from 'src/hooks/useIdleTimer';
import {
  getRoomsTimePeriodLocal,
  getRoomsDate,
} from 'src/store/rooms/selectors';
import { bookRoom, fetchRoomStatuses } from 'src/store/rooms/services';
import {
  roomStatusesPollingStarted,
  roomStatusesPollingStopped,
} from 'src/store/rooms/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { Room } from 'src/types';
import { formatTime } from 'src/utils/formatTime';

import { RoomBookingFormValues } from './RoomBookingSidesheetPanel';

export interface RoomBookingFormProps {
  room?: Room;
  children?: ReactNode;
}

export const RoomBookingForm: FC<RoomBookingFormProps> = ({
  room,
  children,
}) => {
  const dispatch = useAppDispatch();
  const { from: settingsFrom, to: settingsTo } = useSelector(
    getRoomsTimePeriodLocal,
  );
  const date = useSelector(getRoomsDate);

  const book = useCallback(
    async (values: RoomBookingFormValues) => {
      return dispatch(
        bookRoom({
          bookedFor: values.bookedFor?.id as string | undefined,
          time: values.time,
        }),
      ).unwrap();
    },
    [dispatch],
  );

  const startPolling = useCallback(() => {
    dispatch(roomStatusesPollingStarted(false));
  }, [dispatch]);

  const stopPolling = useCallback(() => {
    dispatch(roomStatusesPollingStopped());
  }, [dispatch]);

  const init = useCallback(async () => {
    if (room?.mapPlaceId && date) {
      await dispatch(roomStatusesPollingStopped());
      await dispatch(fetchRoomStatuses());
      await dispatch(roomStatusesPollingStarted(false));
    }
  }, [date, dispatch, room?.mapPlaceId]);

  useEffect(() => {
    init();
  }, [init]);

  useIdleTimer({
    idleMs: 60000,
    onPause: stopPolling,
    onResume: startPolling,
    when: !!room?.id,
  });

  const initialValues = useMemo(
    (): RoomBookingFormValues => ({
      bookedFor: null,
      time:
        settingsFrom && settingsTo
          ? `${formatTime(new Date(settingsFrom))} — ${formatTime(new Date(settingsTo))}`
          : undefined,
      tableInterval: defaultDuration,
    }),
    [settingsFrom, settingsTo],
  );

  return (
    <Form onSubmit={book} initialValues={initialValues}>
      {() => children}
    </Form>
  );
};
